import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { emailContactService } from '@/services/emailContactService'

const contactSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  company_name: z.string().optional(),
  job_title: z.string().optional(),
  phone: z.string().optional(),
  country: z.string().optional(),
  consent_status: z.string(),
})

const emptyDefaults = {
  email: '',
  first_name: '',
  last_name: '',
  company_name: '',
  job_title: '',
  phone: '',
  country: '',
  consent_status: 'PENDING',
}

const ContactFormDialog = ({ open, onOpenChange, contact, customFieldDefinitions = [], onSuccess }) => {
  const isEditMode = !!contact
  const [customValues, setCustomValues] = React.useState({})
  const [loading, setLoading] = React.useState(false)

  const form = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: emptyDefaults,
  })

  useEffect(() => {
    if (!open) return
    if (isEditMode && contact) {
      form.reset({
        email: contact.email || '',
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        company_name: contact.company_name || '',
        job_title: contact.job_title || '',
        phone: contact.phone || '',
        country: contact.country || '',
        consent_status: contact.consent_status || 'PENDING',
      })
      setCustomValues(contact.custom_fields || {})
    } else {
      form.reset(emptyDefaults)
      setCustomValues({})
    }
  }, [open, isEditMode, contact, form])

  const onSubmit = async (values) => {
    setLoading(true)
    try {
      const payload = { ...values, custom_fields: customValues }
      if (isEditMode) {
        await emailContactService.updateContact(contact.id, payload)
        toast.success('Contact updated')
      } else {
        await emailContactService.createContact(payload)
        toast.success('Contact created')
      }
      onSuccess?.()
      onOpenChange(false)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save contact')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? "Update this contact's details." : 'Add a new contact to your email list.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input placeholder="jane@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Jane" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="company_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input placeholder="Acme Inc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="job_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Marketing Lead" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="+1 555 000 0000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Input placeholder="United States" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="consent_status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Consent Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="PENDING">Pending</SelectItem>
                      <SelectItem value="SUBSCRIBED">Subscribed</SelectItem>
                      <SelectItem value="UNSUBSCRIBED">Unsubscribed</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {customFieldDefinitions.length > 0 && (
              <div className="space-y-3 pt-2 border-t">
                <p className="text-sm font-medium text-muted-foreground pt-2">Custom Fields</p>
                {customFieldDefinitions.map((def) => (
                  <div key={def.id} className="space-y-1.5">
                    <label className="text-sm font-medium leading-none">{def.label}</label>
                    {def.field_type === 'BOOLEAN' ? (
                      <div className="flex items-center gap-2 pt-1">
                        <Checkbox
                          checked={!!customValues[def.name]}
                          onCheckedChange={(checked) =>
                            setCustomValues((prev) => ({ ...prev, [def.name]: !!checked }))
                          }
                        />
                      </div>
                    ) : def.field_type === 'SELECT' ? (
                      <Select
                        value={customValues[def.name] || ''}
                        onValueChange={(value) => setCustomValues((prev) => ({ ...prev, [def.name]: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {(def.select_options || []).map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={def.field_type === 'NUMBER' ? 'number' : def.field_type === 'DATE' ? 'date' : 'text'}
                        value={customValues[def.name] ?? ''}
                        onChange={(e) => setCustomValues((prev) => ({ ...prev, [def.name]: e.target.value }))}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditMode ? 'Save Changes' : 'Add Contact'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default ContactFormDialog
